import { PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import { capitalize, IconButton } from '@mui/material';

import { useRouter } from 'next/router';

import { AuthContext, useIsLoggedIn } from '@arrived/auth';
import {
  logoutItem,
  NavigationArrivedLogo,
  NavItem,
  NavItemProps,
  NAV_TYPE,
  PrivateNavItems,
  PrivateNavItemsMobile,
  PrivateSecondaryItems,
  PublicNavItems,
  PublicSecondaryItems,
  useIsMobile,
  NavigationBarDisplay,
} from '@arrived/components';

import { useNotificationBannerContent } from '../NotificationBanner';
import { NavLinkRaw, NavLinkRegular } from './NavLink';

const APP_PATH = process.env.APP_PATH ?? '';

const addComponent = (item: NavItemProps) => {
  if (item.path) {
    item.buttonProps = {
      ...item.buttonProps,
      component: 'a',
      href: item.path,
    };
  }

  if (item.publicPath) {
    item.buttonProps = {
      ...item.buttonProps,
      component: NavLinkRegular,
      href: item.publicPath,
    };
  }

  return item;
};

interface NavMenuProps {
  isPropertyDetailsPage?: boolean;
  showNotificationBanner?: boolean;
}

const applySelected = (item: NavItemProps, path?: string) => {
  const usedPath = item.publicPath ?? item.path;
  const isActive = path === usedPath || (usedPath && path.startsWith(usedPath));
  const returnedItem: NavItemProps = { ...item, isActive };

  if (returnedItem.type === NAV_TYPE.MULTIPLE) {
    returnedItem.subItems = returnedItem.subItems.map((item) => {
      const usedPath = item.publicPath ?? item.path;
      const isActive = path === usedPath || (usedPath && path.startsWith(usedPath));
      return { ...item, isActive };
    });
  }

  return returnedItem;
};

export const NavMenu = ({
  children,
  isPropertyDetailsPage,
  showNotificationBanner,
}: PropsWithChildren<NavMenuProps>) => {
  const { user, isLoading } = useContext(AuthContext);

  const intl = useIntl();
  const isMobile = useIsMobile();
  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();

  const navItemRenderFn = useCallback(
    (item: NavItemProps, idx: number) => {
      const appliedItem = applySelected(item, router.asPath);
      return <NavItem isNewTheme={false} key={idx} {...appliedItem} />;
    },
    [router.asPath]
  );

  const primaryItems = useMemo(() => {
    if (isLoading) return;
    if (isLoggedIn) {
      if (isMobile) {
        return PrivateNavItemsMobile(
          addComponent,
          {
            firstName: capitalize(user?.nickname),
            lastName: user?.family_name,
            isAdmin: false,
            profilePictureUrl: user.picture ?? '',
          },
          APP_PATH
        );
      }
      return PrivateNavItems(addComponent, APP_PATH);
    }

    return PublicNavItems(addComponent);
  }, [isLoading, isMobile, isLoggedIn, addComponent, user?.nickname, user?.family_name]);

  const secondaryItems = useMemo(() => {
    if (isLoggedIn) {
      if (isMobile) return;

      return PrivateSecondaryItems(
        addComponent,
        {
          component: NavLinkRaw,
          href: '/logout',
        },
        {
          firstName: capitalize(user?.nickname),
          lastName: user?.family_name,
          isAdmin: false,
          profilePictureUrl: user.picture ?? '',
        },
        APP_PATH
      );
    }

    return PublicSecondaryItems(
      addComponent,
      {
        component: NavLinkRaw,
        href: '/login',
      },
      {
        component: NavLinkRaw,
        href: '/signup',
      },
      isMobile
    );
  }, [router.asPath, isLoading, isLoggedIn, isMobile, user?.nickname, user?.family_name]);

  const extraItems = useMemo(() => {
    if (isLoading || !isLoggedIn) return;
    return [
      addComponent(
        logoutItem({
          component: NavLinkRaw,
          href: '/logout',
        })
      ),
    ];
  }, [isLoading, isLoggedIn]);

  const desktopLogo = useMemo(
    () => (
      <NavLinkRaw href="/" title={intl.formatMessage({ id: 'arrived', defaultMessage: 'Arrived' })}>
        <NavigationArrivedLogo />
      </NavLinkRaw>
    ),
    []
  );

  const mobileLogo = useMemo(() => {
    if (isPropertyDetailsPage) {
      return (
        <IconButton onClick={() => router.back()}>
          <ArrowBackIosNew />
        </IconButton>
      );
    }
    return desktopLogo;
  }, [desktopLogo, isPropertyDetailsPage, router]);

  const sharedProps = {
    primaryItems,
    secondaryItems,
    extraItems,
    Component: navItemRenderFn,
  };

  const { shownMessage, handleOnClose } = useNotificationBannerContent();

  return (
    <NavigationBarDisplay
      NavigationBarProps={{ isTopNavOnPage: true, desktopLogo, mobileLogo, ...sharedProps }}
      NotificationBannerContentDisplayProps={{
        isNotificationBannerOnPage: showNotificationBanner,
        ...(shownMessage
          ? {
              link: shownMessage.action?.link,
              children: shownMessage.message,
              onClose: handleOnClose,
            }
          : {}),
      }}
    >
      {children}
    </NavigationBarDisplay>
  );
};
